import { TFunction } from 'i18next'
import React, { FC, memo, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Text from '@common/components/Text'
import { getDateLocale } from '@common/utils/dateLocale'

interface Props {
  deadline: bigint
  textSize: number
  marginRight: number
}

// Translated copy of ambire-common's `getDeadlineText` (libs/humanizer/utils), which returns
// English and formats the date with the device locale. Keep the two in step.
const getDeadlineText = (deadline: bigint, t: TFunction): string => {
  const minute = 60000n
  const diff = BigInt(deadline) - BigInt(Date.now())

  if (diff < 0 && diff > -minute * 2n) return t('expired just now')
  if (diff < 0) return t('already expired')
  if (diff < minute) return t('expires in less than a minute')
  if (diff < 30n * minute)
    return t('expires in {{count}} minutes', { count: Math.floor(Number(diff / minute)) })
  if ((deadline / 1000n).toString(16) === 'f'.repeat(64)) return t('No expiration date')
  if (deadline.toString(16) === 'f'.repeat(64)) return t('No expiration date')
  const deadlineDate = new Date(Number(deadline))
  if (Number.isNaN(deadlineDate.getTime())) return t('Invalid expiration date')

  return t('valid until {{date}}', { date: deadlineDate.toLocaleString(getDateLocale()) })
}

const DeadlineItem: FC<Props> = ({ deadline, textSize, marginRight }) => {
  const { t } = useTranslation()
  const [deadlineText, setDeadlineText] = useState(() => getDeadlineText(deadline, t))
  const remainingTime = deadline - BigInt(Date.now())
  const minute: bigint = 60000n

  useEffect(() => {
    let updateAfter = Number(minute)

    // more then 10mints
    if (remainingTime > 10n * minute) updateAfter = Number(remainingTime - 10n * minute)
    // if 0< and <10 minutes
    if (remainingTime > 0 && remainingTime < 10n * minute)
      updateAfter = Number(remainingTime % minute)
    // if just expired
    if (remainingTime < 0 && remainingTime > -2n * minute)
      updateAfter = Number(2n * minute + remainingTime)
    // if long expired
    if (remainingTime < -2n * minute) updateAfter = Number(10n * minute)

    // this triggeres use effect after 'updateAfter' milliseconds by updating the text
    const timeoutId = setTimeout(() => {
      setDeadlineText(getDeadlineText(deadline, t))
    }, updateAfter)

    return () => clearTimeout(timeoutId)
  }, [deadlineText, deadline, minute, remainingTime, t])

  // Re-translate when the language changes
  useEffect(() => {
    setDeadlineText(getDeadlineText(deadline, t))
  }, [t, deadline])

  return (
    <Text fontSize={textSize} weight="medium" appearance="warningText" style={{ marginRight }}>
      {`(${deadlineText})`}
    </Text>
  )
}

export default memo(DeadlineItem)
